import { observer } from "mobx-react-lite";
import type { NodeProps } from "@xyflow/react";
import { BranchHeaderCard } from "../../ActionGroup/Branches/BranchHeaderCard";
import { useRootStore } from "../../../stores/hooks";
import { useFlowCanvasHandlers } from "./context";
import { HiddenHandles } from "./handles";
import type { FlowNodeData } from "../graph/types";

/**
 * React Flow wrapper over the extracted `BranchHeaderCard`. Selection state,
 * deletability (kebab hidden on the last remaining branch) and the delete
 * fallback (`onBranchDeleted` -> re-select the block panel) mirror how
 * `Branches/index.tsx` drives `BranchColumn`.
 */
export const BranchHeaderNode = observer<NodeProps>(({ data }) => {
  const { instanceId, branchHash } = data as Extract<
    FlowNodeData,
    { kind: "branchHeader" }
  >;
  const store = useRootStore();
  const handlers = useFlowCanvasHandlers();
  const block = store.automations.candidate.branches.findBlock(instanceId);
  if (block === undefined) return null;

  const totalBranches =
    block.branches.length + (block.elseBranch !== undefined ? 1 : 0);

  const isSelected =
    handlers.activeItem?.type === "branch" &&
    handlers.activeItem.instanceId === instanceId &&
    handlers.activeItem.branchHash === branchHash;

  return (
    <>
      <HiddenHandles />
      <BranchHeaderCard
        blockInstanceId={instanceId}
        branchHash={branchHash}
        isSelected={isSelected}
        onSelect={handlers.onSelectBranch}
        isDeletable={totalBranches > store.featureFlags.limits.minTotalBranches}
        onBranchDeleted={handlers.onSelectBlock}
      />
    </>
  );
});
BranchHeaderNode.displayName = "BranchHeaderNode";
